import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import authService from "@/features/auth/services/authService";
import { useAuthStore } from "@/features/auth/stores/authStore";

interface SignInVariables {
    email: string;
    password: string;
}

/**
 * Sign-in mutation hook using TanStack Query
 * Handles sign-in logic. Auth store updates automatically via onAuthStateChanged listener.
 */
export function useSignIn() {
    const { setError } = useAuthStore();
    const navigate = useNavigate();

    const mutation = useMutation({
        mutationFn: async ({ email, password }: SignInVariables) => {
            const credential = await authService.signIn(email, password);
            if (!credential.user?.uid) {
                throw new Error("Invalid credential received");
            }
            return credential;
        },
        onError: (error) => {
            const errorMessage = error instanceof Error ? error.message : "Failed to sign in. Please try again.";
            setError(errorMessage);
        },
        onSuccess: () => {
            // Redirect to sessions page on successful login
            navigate("/sessions");
        },
        // onAuthStateChanged will fire automatically when signInWithEmailAndPassword succeeds
        // and the auth store will update automatically
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isLoading: mutation.isPending,
        signIn: mutation.mutate,
        signInAsync: mutation.mutateAsync,
    };
}
